import React, {useMemo} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../components/content-box/ContentBox";
import Dependencies from "../../../../../utilities/Dependencies";
import Environment from "../../../../../services/interfaces/Environment";
import PageActions from "../../../../base/PageActions";
import '../../../../styles/Common.scss';
import '../WorldPages.scss';
import '../WorldDynamicContent.scss';
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";

interface BestiaryPageProps {
    dependencies: Dependencies,
    env: Environment
}

const BestiaryPage = (props: BestiaryPageProps) => {

    const page: PageActions = useMemo(() => new PageActions({
        dependencies: props.dependencies,
        env: props.env,
        title: `Casarok Bestiary - Chase the Fox`,
        description: "The world of Casarok has a wide variety of creatures from the standard fluffy critters to the powerful elemental dragons!",
        keywords: ["casarok", "bestiary", "fantasy races", "monsters", "anthropomorphic races"],
        imgSrc: `chase/world/bestiary/draconic.png`,
        url: `world/bestiary`
    }), [props]);

    return page.generateMetaContent(
        <>
            <ContentBox header="Bestiary">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <p>
                        Life on Casarok takes many different forms. The intelligent species are divided into three
                        races, while the rest of sentient life has their own set of three classifications.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={page.image().getCdnImageSrc(`chase/world/bestiary/anthro.png`)}
                             alt="An Anthro kangaroo"
                             title="An Anthro kangaroo"/>
                    </div>
                    <p className="races-text">
                        The <NavLink className="world-link" to="/world/bestiary/anthro">Anthros</NavLink> are the
                        most common and diverse race of intelligent creatures in Casarok. Most species that make up this
                        race correspond to a species of animal found on Earth; however, there are some species native only
                        to Casarok.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={page.image().getCdnImageSrc(`chase/world/bestiary/aspect.png`)}
                             alt="A Cait Sith, a member of the Aspect race"
                             title="A Cait Sith, a member of the Aspect race"/>
                    </div>
                    <p className="races-text">
                        The <NavLink className="world-link" to="/world/bestiary/aspect">Aspects</NavLink> are the
                        intelligent race of Casarok created to be avatars of
                        the <NavLink className="world-link" to="/world/pantheon">gods and goddesses</NavLink>. Each
                        species of the Aspect race is in the form of a mythical creature that matches their matron or patron
                        deity. Thus, their magic aligns more closely with their deity than the other races.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={page.image().getCdnImageSrc(`chase/world/bestiary/draconic.png`)}
                             alt="A dracamma, one of the six living Draconic species"
                             title="A dracamma, one of the six living Draconic species" />
                    </div>
                    <p className="races-text">
                        The <NavLink className="world-link" to="/world/bestiary/draconic">Draconic</NavLink> race is
                        the least common and diverse of Casarok's three intelligent races. There are only six known species,
                        each one descended from a type of primordial dragon. They have a strict set of skills passed down to
                        them by the gods and goddesses of Casarok and are unable to learn spells outside this range.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={page.image().getCdnImageSrc(`chase/world/bestiary/feral.png`)}
                             alt="Tage, a feral creature"
                             title="Tage, a feral creature" />
                    </div>
                    <p className="races-text">
                        <NavLink className="world-link" to="/world/bestiary/feral">Feral</NavLink> is the
                        classification for all non-magical non-intelligent creatures that naturally evolved from the
                        primordial beings of
                        the <NavLink className="world-link" to="/world/history">Creation Era</NavLink>. Many
                        feral species are docile and capable of domestication. Others are hunted for meat, scales, hide,
                        and other resources.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={page.image().getCdnImageSrc(`chase/world/bestiary/monster.png`)}
                             alt="Humbaba, one of the many dangerous monsters of Casarok"
                             title="Humbaba, one of the many dangerous monsters of Casarok" />
                    </div>
                    <p className="races-text">
                        <NavLink className="world-link" to="/world/bestiary/monster">Monster</NavLink> is the
                        classification for all creatures that evolved from primordial being through the planet's magic.
                        Most of them are non-intelligent and generally hostile or dangerous. They can be hunted for
                        meat, defense, or sport. In more modern times, magical wards keep monsters out of populated
                        areas.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={page.image().getCdnImageSrc(`chase/world/bestiary/floradon.png`)}
                             alt="An aimian, one of the more dangerous floradons inhabiting Casarok"
                             title="An aimian, one of the more dangerous floradons inhabiting Casarok" />
                    </div>
                    <p className="races-text">
                        <NavLink className="world-link" to="/world/bestiary/floradon">Floradon</NavLink> is the
                        classification for all highly evolved plant-based life in Casarok. They are the accidental
                        creations of the god of time, Tempatrius, and the goddess of nature, Domathair. All species
                        are magical in nature with some more capable of wielding magic than others.  As such, some
                        species can be domesticated and kept as pets while others are very dangerous. Those floradons
                        that are not poisonous can be hunted for food.
                    </p>
                </div>
            </ContentBox>
        </>
    );
}

export default BestiaryPage;